import React, { useEffect, useImperativeHandle, forwardRef } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { CatalogueCard, CardGrid } from '../../components/Catalogue';
import { useCatalogueServices } from '../../hooks/useCatalogueServices';
import { NewPagination } from '../../components/Common/Pagination/Pagination';

interface CatalogueServicesProps {
  onItemCountChange?: (count: number) => void;
}

const CatalogueServices = forwardRef<any, CatalogueServicesProps>(({ onItemCountChange }, ref) => {
  const navigate = useNavigate();

  const { 
    services, 
    loading, 
    approveService, 
    suspendService, 
    approveAllServices,
    pagination,
  } = useCatalogueServices();

  // Notify parent of item count changes
  useEffect(() => {
    if (onItemCountChange) {
      onItemCountChange(services.length);
    }
  }, [services.length, onItemCountChange]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    handleApproveAll: approveAllServices,
  }));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-4-value)', flex: 1, overflow: 'hidden' }}>
      {/* Service Cards Grid */}
      {services.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <Typography>No services pending approval</Typography>
        </Box>
      ) : (
        <CardGrid minCardWidth="245px">
          {services.map((service) => (
            <CatalogueCard
              key={service.productId}
              id={service.productId}
              image={service.imageURL}
              title={service.title}
              subtitle={service.vendorName}
              price={`QAR ${service.price.toFixed(2)}`}
              description={service.description}
              packageName={service.packageName}
              variant="service"
              onCardClick={(id) => navigate(`/catalogue/services/${id}`)}
              onApprove={approveService}
              onSuspend={suspendService}
            />
          ))}
        </CardGrid>
      )}

      <NewPagination
        {...pagination}
      />
    </Box>
  );
});

CatalogueServices.displayName = 'CatalogueServices';

export default CatalogueServices;
